/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { createFileRoute, redirect } from '@tanstack/react-router';
import { BILLING_DEFAULT_SECTION } from '@/features/system-settings/billing/section-registry.tsx';
export const Route = createFileRoute('/_authenticated/system-settings/billing/')({
  beforeLoad: () => {
    throw redirect({
      to: '/system-settings/billing/$section',
      params: {
        section: BILLING_DEFAULT_SECTION
      }
    });
  }
});
if (import.meta.webpackHot) {
  const hot = import.meta.webpackHot;
  const hotData = hot.data ??= {};
  const routeId = hotData['tsr-route-id'] ?? Route.id ?? (Route.isRoot ? '__root__' : "/_authenticated/system-settings/billing/");
  if (routeId) {
    hotData['tsr-route-id'] = routeId;
  }
  const existingRoute = typeof window !== 'undefined' && routeId ? window.__TSR_ROUTER__?.routesById?.[routeId] : undefined;
  if (routeId && existingRoute && existingRoute !== Route) {
    (function handleRouteUpdate(routeId, newRoute) {
      const router = window.__TSR_ROUTER__;
      const oldRoute = router.routesById[routeId];
      if (!oldRoute) return;
      const generatedRouteOptionKeys = new Set(["id", "path", "getParentRoute"]);
      const generatedRouteOptions = {};
      generatedRouteOptionKeys.forEach(key => {
        if (key in oldRoute.options) generatedRouteOptions[key] = oldRoute.options[key];
      });
      const removedKeys = new Set();
      Object.keys(oldRoute.options).forEach(key => {
        if (!generatedRouteOptionKeys.has(key) && !(key in newRoute.options)) {
          removedKeys.add(key);
          delete oldRoute.options[key];
        }
      });
      const preserveComponentIdentity = "shellComponent" in oldRoute.options === "shellComponent" in newRoute.options;
      const componentKeys = ["component", "shellComponent", "pendingComponent", "errorComponent", "notFoundComponent"];
      if (preserveComponentIdentity) componentKeys.forEach(key => {
        if (key in oldRoute.options && key in newRoute.options) newRoute.options[key] = oldRoute.options[key];
      });
      const nextOptions = {
        ...newRoute.options,
        ...generatedRouteOptions
      };
      oldRoute.options = nextOptions;
      oldRoute.update(nextOptions);
      oldRoute._componentsPromise = void 0;
      oldRoute._lazyPromise = void 0;
      router.setRoutes(router.buildRouteTree());
      syncHotRouteExport(oldRoute);
      router.resolvePathCache.clear();
      const filter = m => m.routeId === oldRoute.id;
      const activeMatch = router.stores.matches.get().find(filter);
      const pendingMatch = router.stores.pendingMatches.get().find(filter);
      const cachedMatches = router.stores.cachedMatches.get().filter(filter);
      if (activeMatch || pendingMatch || cachedMatches.length > 0) {
        if (removedKeys.has("loader") || removedKeys.has("beforeLoad")) {
          const matchIds = [activeMatch?.id, pendingMatch?.id, ...cachedMatches.map(match => match.id)].filter(Boolean);
          router.batch(() => {
            for (const matchId of matchIds) {
              const store = router.stores.pendingMatchStores.get(matchId) || router.stores.matchStores.get(matchId) || router.stores.cachedMatchStores.get(matchId);
              if (store) store.set(prev => {
                const next = {
                  ...prev
                };
                if (removedKeys.has("loader")) next.loaderData = void 0;
                if (removedKeys.has("beforeLoad")) {
                  next.__beforeLoadContext = void 0;
                  next.context = rebuildMatchContextWithoutBeforeLoad(next);
                }
                return next;
              });
            }
          });
        }
        router.invalidate({
          filter,
          sync: true
        });
      }
      function syncHotRouteExport(liveRoute) {
        newRoute.options = liveRoute.options;
        newRoute.parentRoute = liveRoute.parentRoute;
        newRoute._path = liveRoute._path;
        newRoute._id = liveRoute._id;
        newRoute._fullPath = liveRoute._fullPath;
        newRoute._to = liveRoute._to;
      }
      function getStoreMatch(matchId) {
        return router.stores.pendingMatchStores.get(matchId)?.get() || router.stores.matchStores.get(matchId)?.get() || router.stores.cachedMatchStores.get(matchId)?.get();
      }
      function getMatchList(matchId) {
        const pendingMatches = router.stores.pendingMatches.get();
        if (pendingMatches.some(match => match.id === matchId)) return pendingMatches;
        const activeMatches = router.stores.matches.get();
        if (activeMatches.some(match => match.id === matchId)) return activeMatches;
        const cachedMatches = router.stores.cachedMatches.get();
        if (cachedMatches.some(match => match.id === matchId)) return cachedMatches;
        return [];
      }
      function getParentMatch(match) {
        const matchList = getMatchList(match.id);
        const matchIndex = matchList.findIndex(item => item.id === match.id);
        if (matchIndex <= 0) return;
        const parentMatch = matchList[matchIndex - 1];
        return getStoreMatch(parentMatch.id) || parentMatch;
      }
      function rebuildMatchContextWithoutBeforeLoad(match) {
        const parentMatch = getParentMatch(match);
        const getParentContext = router.getParentContext;
        return {
          ...((getParentContext ? getParentContext.call(router, parentMatch) : parentMatch?.context ?? router.options.context) ?? {}),
          ...(match.__routeContext ?? {})
        };
      }
    })(routeId, Route);
    try {
      const tsrReactRefreshUtils = typeof __react_refresh_utils__ !== 'undefined' ? __react_refresh_utils__ : undefined;
      const tsrEnqueueUpdate = tsrReactRefreshUtils && typeof tsrReactRefreshUtils.enqueueUpdate === 'function' ? tsrReactRefreshUtils.enqueueUpdate : undefined;
      if (tsrEnqueueUpdate) {
        tsrEnqueueUpdate(() => {});
      }
    } catch (_err) {}
  }
  hot.dispose(data => {
    if (routeId) {
      data['tsr-route-id'] = routeId;
    }
  });
  hot.accept();
}